"use client";

import ListAvatar from "@/components/peeqd/tracking/ListAvatar";
import { formatRigaDateTime } from "@/libs/tracking/formatRiga";
import { INSTAGRAM_LINK_PROPS, instagramProfileUrl } from "@/libs/instagramUrl";
import type { FollowChangeItem, RelationshipItem } from "./types";

type Tab = "changelog" | "followers" | "following";

interface AccountDetailProps {
  username: string;
  accountId: string;
  isScraping: boolean;
  hasEmptyData: boolean;
  onRescrape: () => void;
  rescrapeLoading: boolean;
  activeTab: Tab;
  onTabChange: (tab: Tab) => void;
  changelog: FollowChangeItem[];
  followers: RelationshipItem[];
  following: RelationshipItem[];
  loading: boolean;
}

export default function AccountDetail({
  username,
  isScraping,
  hasEmptyData,
  onRescrape,
  rescrapeLoading,
  activeTab,
  onTabChange,
  changelog,
  followers,
  following,
  loading,
}: AccountDetailProps) {
  const tabs: { id: Tab; label: string; count?: number }[] = [
    { id: "changelog", label: "Changes", count: changelog.length },
    { id: "followers", label: "Followers", count: followers.length },
    { id: "following", label: "Following", count: following.length },
  ];

  return (
    <div className="rounded-2xl border border-line bg-surface shadow-soft">
      <div className="border-b border-line px-4 py-4 sm:px-6">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="font-display text-xl font-bold text-ink">@{username}</h2>
          <div className="flex items-center gap-2">
            {hasEmptyData && !isScraping && (
              <button
                type="button"
                onClick={onRescrape}
                disabled={rescrapeLoading}
                className="rounded-xl border border-line bg-bg px-3 py-1.5 text-xs font-semibold text-ink hover:border-rose/30 disabled:opacity-50"
              >
                {rescrapeLoading ? "Queuing…" : "Re-scrape"}
              </button>
            )}
            {isScraping && (
              <span className="rounded-full bg-rose/10 px-3 py-1 text-xs font-semibold text-rose">
                Analyzing…
              </span>
            )}
          </div>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => onTabChange(tab.id)}
              data-active={activeTab === tab.id ? "true" : "false"}
              className="rounded-xl border border-line px-3 py-1.5 text-sm font-semibold text-muted data-[active=true]:border-rose/30 data-[active=true]:bg-rose/5 data-[active=true]:text-ink"
            >
              {tab.label}
              {tab.count != null ? ` (${tab.count})` : ""}
            </button>
          ))}
        </div>
      </div>

      <div className="p-4 sm:p-6">
        {loading ? (
          <p className="text-sm text-muted">Loading…</p>
        ) : activeTab === "changelog" ? (
          <ChangeLogList items={changelog} />
        ) : (
          <RelationshipList
            items={activeTab === "followers" ? followers : following}
            emptyLabel={activeTab}
          />
        )}
      </div>
    </div>
  );
}

function ChangeLogList({ items }: { items: FollowChangeItem[] }) {
  if (items.length === 0) {
    return (
      <p className="text-sm text-muted">
        No changes yet. Run the worker after adding this account.
      </p>
    );
  }

  return (
    <ul className="space-y-3">
      {items.map((item) => (
        <li
          key={item.id}
          className="flex items-start gap-3 rounded-xl border border-line bg-bg/60 px-3 py-2.5"
        >
          <span
            className={`mt-0.5 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase ${
              item.changeType === "follow"
                ? "bg-green-100 text-green-800"
                : "bg-red-100 text-red-800"
            }`}
          >
            {item.changeType}
          </span>
          <div className="min-w-0 flex-1">
            <a
              href={instagramProfileUrl(item.username)}
              {...INSTAGRAM_LINK_PROPS}
              className="font-medium text-ink hover:text-rose-deep"
            >
              @{item.username}
            </a>
            <p className="text-xs text-muted">
              {item.side} · {formatRigaDateTime(item.detectedAt)}
            </p>
          </div>
        </li>
      ))}
    </ul>
  );
}

function RelationshipList({
  items,
  emptyLabel,
}: {
  items: RelationshipItem[];
  emptyLabel: string;
}) {
  if (items.length === 0) {
    return <p className="text-sm text-muted">No {emptyLabel} loaded yet.</p>;
  }

  return (
    <ul className="space-y-2">
      {items.map((item) => (
        <li
          key={item.id}
          className="flex items-center gap-3 rounded-xl border border-line bg-bg/60 px-3 py-2"
        >
          <ListAvatar avatarUrl={item.avatarUrl} size="sm" />
          <a
            href={instagramProfileUrl(item.username)}
            {...INSTAGRAM_LINK_PROPS}
            className="min-w-0 flex-1 hover:opacity-90"
          >
            <p className="truncate font-medium text-ink">@{item.username}</p>
            <p className="truncate text-xs text-muted">
              {item.displayName}
              {item.isRecent ? " · RECENT" : ""}
            </p>
          </a>
        </li>
      ))}
    </ul>
  );
}
